/**
 * toastService.ts - Simple toast notification system
 * Emit and listen to toast events
 */

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

type ToastListener = (toast: Toast) => void;
type ToastRemoveListener = (id: string) => void;

class ToastService {
  private listeners: ToastListener[] = [];
  private removeListeners: ToastRemoveListener[] = [];
  private toastIdCounter = 0;

  /**
   * Show a toast notification
   */
  show(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', duration = 3000) {
    const id = `toast-${++this.toastIdCounter}`;
    const toast: Toast = { id, message, type, duration };

    // Notify all listeners
    this.listeners.forEach(listener => listener(toast));

    // Auto-remove after duration (if duration > 0)
    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }

    return id;
  }

  /**
   * Remove a toast by id
   */
  remove(id: string) {
    this.removeListeners.forEach(listener => listener(id));
  }

  /**
   * Subscribe to toast events
   */
  subscribe(listener: ToastListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Subscribe to toast removal events
   */
  subscribeRemove(listener: ToastRemoveListener): () => void {
    this.removeListeners.push(listener);
    return () => {
      this.removeListeners = this.removeListeners.filter(l => l !== listener);
    };
  }

  // Convenience methods
  success(message: string, duration?: number) {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration?: number) {
    return this.show(message, 'error', duration);
  }

  warning(message: string, duration?: number) {
    return this.show(message, 'warning', duration);
  }

  info(message: string, duration?: number) {
    return this.show(message, 'info', duration);
  }
}

export const toastService = new ToastService();
export default toastService;
